// Spanish copy for /preview-first-uploads. Same shape as copy.en.ts (checked by copy.test.ts).
import type { PreviewFirstCopy } from "./copy.en";

const es: PreviewFirstCopy = {
  seo: {
    title: "Subidas con vista previa, en todos los planes | Photohawk",
    description:
      "Novedad en la subida web de Photohawk, en todos los planes: primero se sube una vista previa pequeña de cada foto, para que tu galería pueda publicarse, vender y compartirse en minutos mientras los originales llegan en segundo plano.",
  },
  hero: {
    badge: "Nuevo",
    eyebrow: "En la subida web · Todos los planes",
    title: "Tu galería en línea en minutos,",
    titleHighlight: "no en horas",
    subtitle:
      "La subida web ahora envía primero una vista previa pequeña de cada foto. Tu galería puede publicarse con esas vistas previas, para que los compradores encuentren, compren y compartan sus fotos mientras los originales se siguen subiendo en segundo plano.",
    ctaPrimary: "Abrir la subida web",
    ctaSecondary: "Ver cómo funciona",
    /** Back to the web uploader launch page (/web-uploader). */
    uploaderLink: "Mira todas las novedades de la subida web",
  },
  steps: {
    heading: "Cómo funciona",
    subtitle: "La vista previa primero está en la subida web, en todos los planes. Al añadir fotos, te muestra cuánto tarda la subida de cada forma y eliges «Previews first, originals later».",
    stepLabel: "Paso {n}",
    items: [
      {
        id: "add",
        title: "Añade tus fotos",
        body: "Arrastra fotos o una carpeta entera, como siempre. Tu navegador crea una vista previa pequeña de cada una, en tu propio ordenador.",
      },
      {
        id: "previews",
        title: "Primero suben las vistas previas",
        body: "Las vistas previas son pequeñas, así que suben rápido, incluso con una conexión lenta en el lugar del evento.",
      },
      {
        id: "live",
        title: "Tu galería se publica",
        body: "Publícala en cuanto estén las vistas previas, o marca la casilla para que se publique sola en ese momento. Los compradores pueden navegar, buscar, comprar y compartir de inmediato.",
      },
      {
        id: "full",
        title: "Después llegan los originales",
        body: "Los originales se siguen subiendo en segundo plano, en las mismas fotos. Las descargas y las impresiones siempre usan los originales.",
      },
    ],
    timeline: {
      label: "Una subida de ejemplo, de principio a fin",
      previews: "Vistas previas",
      previewsTime: "minutos",
      live: "Galería en línea",
      full: "Originales",
      fullTime: "en segundo plano",
    },
  },
  buyers: {
    heading: "Lo que ven los compradores",
    subtitle: "Nada nuevo que aprender. Los compradores ven la galería de siempre, solo que antes.",
    items: [
      {
        id: "protected",
        title: "Con marca de agua y protegidas, como siempre",
        body: "Los compradores ven las mismas vistas previas con marca de agua de siempre, y la protección de vistas previas funciona igual si tu galería la usa.",
      },
      {
        id: "search",
        title: "Se encuentran de inmediato",
        body: "La búsqueda por selfie y por dorsal funciona con las vistas previas, así que la gente encuentra sus fotos en cuanto la galería está en línea.",
      },
      {
        id: "delivery",
        title: "Originales, entregados automáticamente",
        body: "Una compra o una descarga gratuita nunca se entrega como vista previa. Si alguien compra o descarga una foto antes de que llegue su original, este pasa al principio de la cola y la persona lo recibe en cuanto está listo.",
      },
    ],
  },
  strip: {
    heading: "Síguelo mientras subes",
    body: "Mientras se suben las vistas previas y los originales, una franja en la parte superior de la subida web muestra cómo va tu galería: si ya está en línea, cuántos originales faltan y cuándo una foto comprada ha pasado al principio. Cuando la galería ya está en línea, también muestra cuántas personas la han visitado en los últimos 30 minutos, y los propietarios y administradores del espacio de trabajo ven los pedidos nuevos desde que empezó la subida.",
    exampleLabel: "Ejemplo",
    live: "En línea con vistas previas",
    orders: "3 pedidos desde que empezaste",
    visitors: "42 visitantes en los últimos 30 min",
    left: "Faltan 1250 originales",
    jumped: "Los originales de 2 fotos pedidas pasaron al principio",
  },
  faq: {
    heading: "Preguntas",
    items: [
      {
        q: "¿Tengo que dejar la subida web abierta?",
        a: "Es mejor hacerlo hasta que terminen los originales; la franja muestra cuántos faltan. Si la cierras antes, no se pierde nada. Tu galería sigue en línea con las vistas previas y, la próxima vez que abras esa galería en la subida web, te dirá cuántas fotos siguen esperando. Vuelve a añadir la misma carpeta (aunque sea más tarde, desde casa) y solo se enviarán los originales que faltan.",
      },
      {
        q: "¿Y si alguien compra una foto antes de que llegue su original?",
        a: "Puede comprarla igualmente. Mientras la subida web está en marcha, el original de esa foto pasa al principio de la cola. El correo del pedido espera al archivo y sale a los pocos minutos de que llegue, así que el comprador nunca recibe la vista previa por error. Si la subida web se cerró, el pedido espera hasta que vuelvas a añadir la carpeta. La subida web te indica qué fotos está esperando un comprador, y esas se envían primero.",
      },
      {
        q: "¿Funciona en galerías de descarga gratuita?",
        a: "Sí. La gente puede ver, buscar y descargar en cuanto están las vistas previas, y una descarga nunca es la vista previa. Si alguien descarga una foto antes de que llegue su original, este pasa al principio de la cola: su descarga muestra que el original aún se está subiendo y se convierte en una descarga normal en cuanto llega el archivo, y una descarga por correo sale en ese momento. Cada descarga cuenta una sola vez.",
      },
      {
        q: "¿La búsqueda por selfie y por dorsal funciona con las vistas previas?",
        a: "Sí. Las caras y los dorsales se detectan en las vistas previas, así que la búsqueda funciona en cuanto están subidas. Mientras la subida web está abierta, también envía primeros planos más nítidos sacados de tus originales para ayudar a la búsqueda.",
      },
      {
        q: "¿Qué planes la incluyen?",
        a: "Todos los planes, también Lite. Si tu espacio de trabajo ya usó la vista previa primero, esas fotos terminan con normalidad: vuelve a añadir la misma carpeta y se envían sus originales, y sus pedidos y descargas siguen llegando.",
      },
      {
        q: "¿Cuesta algo más?",
        a: "No. Está incluido en todos los planes, y cada foto cuenta una sola vez: la vista previa y su original son la misma foto. La única diferencia es que tu conexión envía un poco más de datos en total (alrededor de un 5 % más).",
      },
      {
        q: "¿Funciona con FTP y con Photohawk Studio?",
        a: "Por ahora no. La vista previa primero forma parte de la subida web. Las fotos enviadas por FTP o desde Photohawk Studio se suben como originales de una vez, como hasta ahora.",
      },
      {
        q: "¿Está activada por defecto?",
        a: "No. Cada vez que añades fotos, la subida web te muestra cuánto tardarán con y sin vista previa primero, y está seleccionado enviar los originales directamente. Elige «Previews first, originals later» cuando quieras la galería en línea antes; la subida web recuerda tu elección para la próxima vez. Con una resolución de subida menor (Large o Web) solo se suben archivos redimensionados, así que entonces la vista previa primero no se aplica.",
      },
    ],
  },
  cta: {
    heading: "Pruébala en tu próxima subida",
    body: "La vista previa primero está en la subida web, en todos los planes. Añade tus fotos, elige la vista previa primero y tu galería puede estar en línea en minutos.",
    button: "Abrir la subida web",
  },
};

export default es;
